import type { HeroDirectoryEntry } from './heroes/directory-types';
import { heroArchiveUrl } from './heroes/routes';
import { staticUrl } from './config/static-assets';
import type { CatalogNavigationItem } from './catalog-navigation';
import { rewriteCatalogCdnUrl } from './config/cdn';

export type CatalogFetcher = <T>(path: string) => Promise<T>;
const fetchCatalogJSON: CatalogFetcher = async <T>(path: string): Promise<T> => {
  const response = await fetch(path, { cache: 'no-store', credentials: 'same-origin' });
  if (!response.ok) throw new Error('Catalog request failed: ' + response.status);
  return response.json() as Promise<T>;
};

export type CatalogUpdate = {
  id: string;
  catalogKey: string;
  catalogName: string;
  name: string;
  image: string;
  href: string;
  updatedAt: string;
  order: number;
};

type CatalogItem = Record<string, unknown>;

function stringValue(value: unknown) {
  return typeof value === 'string' || typeof value === 'number'
    ? String(value).trim()
    : '';
}

function imageValue(item: CatalogItem) {
  const assets = item.assets && typeof item.assets === 'object'
    ? item.assets as CatalogItem
    : {};
  return stringValue(item.image || item.head || item.thumbnail || assets.illustration);
}

function dateValue(value: unknown) {
  const raw = stringValue(value);
  return raw && Number.isFinite(Date.parse(raw)) ? raw : '';
}

function updateHref(gameDomain: string, catalog: CatalogNavigationItem, id: string) {
  const base = `/${encodeURIComponent(gameDomain)}/dcatalog/${encodeURIComponent(catalog.catalogKey)}`;
  if (catalog.displayMode === 'standalone' && /^[a-z]$/.test(catalog.detailPrefix || '') && /^[1-9]\d*$/.test(id)) {
    return `/${encodeURIComponent(gameDomain)}/${catalog.detailPrefix}${id}`;
  }
  return `${base}?catalogItem=${encodeURIComponent(id)}`;
}

async function catalogItems(gameKey: string, catalog: CatalogNavigationItem, fetchJSON: CatalogFetcher) {
  if (gameKey === 'arcanatactics' && ['kapai', 'catalog_65fcfc5c37224d95987a03711aec9e67', 'catalog_ab547affe0d548a4bcdea0fb4ee1d12e'].includes(catalog.catalogKey)) {
    const key = catalog.catalogKey === 'catalog_65fcfc5c37224d95987a03711aec9e67' ? 'kapai' : catalog.catalogKey;
    try {
      const payload = await fetchJSON<{ items?: CatalogItem[] }>(
        `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(key)}/items`,
      );
      return Array.isArray(payload.items) ? payload.items : [];
    } catch {
      return [];
    }
  }
  const base = `/api/games/${encodeURIComponent(gameKey)}/catalogs/${encodeURIComponent(catalog.catalogKey)}`;
  // 按适配器打正确端点；仅 404 时再尝试另一形态，避免 400 试错噪声
  const primary = catalog.adapterKey === 'generic-json-v1'
    ? `${base}/generic/items`
    : `${base}/items`;
  const fallback = catalog.adapterKey === 'generic-json-v1'
    ? `${base}/items`
    : `${base}/generic/items`;
  try {
    const payload = await fetchJSON<{ items?: CatalogItem[] }>(primary);
    if (Array.isArray(payload.items)) return payload.items;
  } catch (error) {
    const message = error instanceof Error ? error.message : '';
    if (!message.includes('404')) return [];
  }
  try {
    const payload = await fetchJSON<{ items?: CatalogItem[] }>(fallback);
    if (Array.isArray(payload.items)) return payload.items;
  } catch {
    // ignore
  }
  return [];
}

export async function getCatalogUpdates(
  gameKey: string,
  gameDomain: string,
  catalogs: CatalogNavigationItem[],
  heroes: HeroDirectoryEntry[],
  fetchJSON: CatalogFetcher = fetchCatalogJSON,
  rewriteImage: (url: string) => string = rewriteCatalogCdnUrl,
): Promise<CatalogUpdate[]> {
  const sourceCatalogs = gameKey === 'arcanatactics'
    ? catalogs.filter((catalog) => catalog.catalogKey === 'hero' && catalog.adapterKey !== 'generic-json-v1')
    : catalogs;
  const results = await Promise.all(sourceCatalogs.map(async (catalog, catalogIndex) => {
    if (catalog.catalogKey === 'hero' && catalog.adapterKey !== 'generic-json-v1') {
      return heroes.map((hero, index) => ({
        id: hero.id,
        catalogKey: catalog.catalogKey,
        catalogName: catalog.name,
        name: hero.name,
        image: hero.head,
        href: heroArchiveUrl(hero, heroes).replace(/^\/[^/]+/, `/${encodeURIComponent(gameDomain)}`),
        updatedAt: dateValue(hero.updatedAt),
        order: catalogIndex * 100_000 + index,
      }));
    }
    const items = await catalogItems(gameKey, catalog, fetchJSON);
    return items.flatMap((item, index) => {
      const id = stringValue(item.id);
      const name = stringValue(item.name || item.title);
      if (!id || !name) return [];
      return [{
        id,
        catalogKey: catalog.catalogKey,
        catalogName: catalog.name,
        name,
        image: rewriteImage(imageValue(item) || catalog.iconUrl || staticUrl('/legacy-game-wiki/assets/home-tiles/catalog-equipment.jpg')),
        href: updateHref(gameDomain, catalog, id),
        updatedAt: dateValue(item.updatedAt || item.sourceUpdatedAt),
        order: catalogIndex * 100_000 + index,
      }];
    });
  }));
  if (gameKey === 'arcanatactics') return results.flat().slice(0, 8);
  const byRecent = (left: CatalogUpdate, right: CatalogUpdate) =>
    (Date.parse(right.updatedAt) || 0) - (Date.parse(left.updatedAt) || 0) || right.order - left.order;
  const sorted = results.flat().sort(byRecent);
  return sorted.slice(0, 8);
}
